/* hebrewAmount.js
 * Hebrew number words + currency phrasing (JS only).
 * Range: 0 .. 999,999,999,999,999 (up to טריליון).
 */

const DEFAULT_OPTS = {
    shekelWord: { singular: "שקל", plural: "שקלים", gender: "m" },
    agoraWord: { singular: "אגורה", plural: "אגורות", gender: "f" },
    formalThousandsConstruct: false, // e.g. "שלושת אלפים" instead of "שלושה אלפים"
    sayZeroAgorot: false,
};

const units = {
    m: ["אפס", "אחד", "שניים", "שלושה", "ארבעה", "חמישה", "שישה", "שבעה", "שמונה", "תשעה"],
    f: ["אפס", "אחת", "שתיים", "שלוש", "ארבע", "חמש", "שש", "שבע", "שמונה", "תשע"],
};

const ten = { m: "עשרה", f: "עשר" };

const teens = {
    m: ["", "אחד עשר", "שניים עשר", "שלושה עשר", "ארבעה עשר", "חמישה עשר", "שישה עשר", "שבעה עשר", "שמונה עשר", "תשעה עשר"],
    f: ["", "אחת עשרה", "שתים עשרה", "שלוש עשרה", "ארבע עשרה", "חמש עשרה", "שש עשרה", "שבע עשרה", "שמונה עשרה", "תשע עשרה"],
};

const tens = ["", "", "עשרים", "שלושים", "ארבעים", "חמישים", "שישים", "שבעים", "שמונים", "תשעים"];

// IMPORTANT: 200 = "מאתיים" (not "מתיים")
const hundreds = ["", "מאה", "מאתיים", "שלוש מאות", "ארבע מאות", "חמש מאות", "שש מאות", "שבע מאות", "שמונה מאות", "תשע מאות"];

const thousandConstructMap = { 3: "שלושת", 4: "ארבעת", 5: "חמשת", 6: "ששת", 7: "שבעת", 8: "שמונת", 9: "תשעת" };

const scales = ["", "אלף", "מיליון", "מיליארד", "טריליון"];

function joinWithVav(parts) {
    const clean = parts.filter(Boolean);
    if (clean.length <= 1) return clean[0] || "";
    const out = [clean[0]];
    for (let i = 1; i < clean.length; i++) out.push("ו" + clean[i]);
    return out.join(" ");
}

function numberUnder100ToWords(n, gender) {
    if (n < 10) return units[gender][n];
    if (n === 10) return ten[gender];
    if (n > 10 && n < 20) return teens[gender][n - 10];

    const t = Math.floor(n / 10);
    const u = n % 10;
    if (u === 0) return tens[t];
    return joinWithVav([tens[t], units[gender][u]]);
}

function numberUnder1000ToWords(n, gender) {
    if (n < 100) return numberUnder100ToWords(n, gender);
    const h = Math.floor(n / 100);
    const r = n % 100;
    const parts = [hundreds[h]];
    if (r > 0) parts.push(numberUnder100ToWords(r, gender));
    return joinWithVav(parts);
}

function cardinalBeforeNoun(n, nounGender, formalThousandsConstruct) {
    if (n < 1000) return numberUnder1000ToWords(n, nounGender);

    // split to triplets
    const chunks = [];
    let tmp = n;
    while (tmp > 0) { chunks.push(tmp % 1000); tmp = Math.floor(tmp / 1000); }

    const parts = [];
    for (let i = chunks.length - 1; i >= 0; i--) {
        const val = chunks[i];
        if (!val) continue;
        const scaleIdx = i; // 0=units, 1=thousand, 2=million, 3=billion ...

        if (scaleIdx === 0) {
            parts.push(numberUnder1000ToWords(val, nounGender));
            continue;
        }

        if (scaleIdx === 1) {
            // Thousands
            if (val === 1) { parts.push("אלף"); continue; }
            if (val === 2) { parts.push("אלפיים"); continue; }

            if (formalThousandsConstruct && val < 10 && thousandConstructMap[val]) {
                parts.push(`${thousandConstructMap[val]} אלפים`);
            } else {
                parts.push(`${numberUnder1000ToWords(val, "m")} אלפים`);
            }
            continue;
        }

        // 1 scale-unit -> "מיליון"; 2 -> "שני מיליון"; 3+ -> "<num masc> מיליון"
        if (val === 1) {
            parts.push(scales[scaleIdx]);
        } else if (val === 2) {
            parts.push(`שני ${scales[scaleIdx]}`);
        } else {
            parts.push(`${numberUnder1000ToWords(val, "m")} ${scales[scaleIdx]}`);
        }
    }

    return joinWithVav(parts);
}

export function numberToHebrewWords(n, gender, opts = {}) {
    if (!Number.isFinite(n) || n < 0 || Math.floor(n) !== n) throw new Error("numberToHebrewWords expects a non-negative integer");
    if (n === 0) return units[gender][0];
    const formalThousandsConstruct = !!opts.formalThousandsConstruct;
    return cardinalBeforeNoun(n, gender, formalThousandsConstruct);
}

// --- Helpers for "<count> <noun>" with Hebrew grammar ---
// n=1   => "שקל אחד" / "אגורה אחת"
// n=2   => "שני שקלים" / "שתי אגורות"
// n>=3  => "<מספר> <רבים>"
function formatCountNoun(n, nounSingular, nounPlural, nounGender, opts = {}) {
    if (n === 0) return `אפס ${nounPlural}`;
    if (n === 1) return `${nounSingular} ${nounGender === "f" ? "אחת" : "אחד"}`;
    if (n === 2) return `${nounGender === "f" ? "שתי" : "שני"} ${nounPlural}`;
    return `${numberToHebrewWords(n, nounGender, opts)} ${nounPlural}`;
}

export function amountToHebrewWords(amount, opts = {}) {
    const o = { ...DEFAULT_OPTS, ...opts };
    if (!Number.isFinite(amount)) throw new Error("Invalid amount");
  
    const isNegative = amount < 0;
    const abs = Math.abs(amount);
  
    const totalAgorot = Math.round(abs * 100);
    const shekels = Math.floor(totalAgorot / 100);
    const agorot = totalAgorot % 100;
  
    const shekelPart = shekels > 0
      ? formatCountNoun(shekels, o.shekelWord.singular, o.shekelWord.plural, o.shekelWord.gender, o)
      : undefined;
  
    let agoraPart;
    if (agorot === 0) {
      // no “ולא אגורות” anymore (unless you explicitly set sayZeroAgorot: true)
      if (o.sayZeroAgorot && shekelPart) {
        agoraPart = `ולא ${o.agoraWord.plural}`;
      }
    } else if (agorot === 1) {
      agoraPart = `ו${o.agoraWord.singular} ${o.agoraWord.gender === "f" ? "אחת" : "אחד"}`;
    } else if (agorot === 2) {
      agoraPart = `ו${o.agoraWord.gender === "f" ? "שתי" : "שני"} ${o.agoraWord.plural}`;
    } else {
      agoraPart = "ו" + numberToHebrewWords(agorot, o.agoraWord.gender, o) + " " + o.agoraWord.plural;
    }
  
    const phrase = [shekelPart, agoraPart].filter(Boolean).join(" ");
    const result = phrase || `אפס ${o.shekelWord.plural}`;
    return isNegative ? `שלילי ${result}` : result;
  }


